import { BuildingDesign } from '@shared/models/design/building-design.model';
import { calculateBoverketBtaFromDesign } from './boverket-climate-declaration-bta.helpers';
import {
  buildBoverketBuildingFieldsFromDesign,
  buildBoverketClimateFieldsFromDesign,
  type BoverketBuildingFields,
  type BoverketClimateFields,
} from './boverket-climate-declaration-building.helpers';
import {
  getEnergiklassForBoverketExport,
  type BoverketEnergiklassInput,
} from './boverket-climate-declaration-energy-class.helpers';

export type BoverketClimateDeclarationExportInput = BoverketEnergiklassInput;

export type BoverketClimateDeclarationPayload = {
  byggnad: BoverketBuildingFields;
  klimat: BoverketClimateFields;
};

const escapeXmlText = (value: string): string =>
  value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');

const xmlTag = (name: string, value: number | string): string => {
  const content =
    typeof value === 'number'
      ? String(Math.round(value))
      : escapeXmlText(value);
  return `<${name}>${content}</${name}>`;
};

/**
 * Build the Boverket klimatdeklaration XML document from design and optional version energy data.
 */
export const buildBoverketClimateDeclarationPayloadFromDesign = (
  input: BuildingDesign | BoverketClimateDeclarationExportInput,
): BoverketClimateDeclarationPayload => {
  const exportInput: BoverketClimateDeclarationExportInput =
    'design' in input ? input : { design: input };

  const { design, version, proposal } = exportInput;
  const bta = calculateBoverketBtaFromDesign(design);
  const energiklass = getEnergiklassForBoverketExport(exportInput);
  const versionContext = { design, version, proposal };

  return {
    byggnad: buildBoverketBuildingFieldsFromDesign(
      design,
      bta,
      energiklass,
      versionContext,
    ),
    klimat: buildBoverketClimateFieldsFromDesign(versionContext),
  };
};

/**
 * Serialize payload to Boverket import XML (250512 structure).
 */
export const buildBoverketClimateDeclarationXml = (
  payload: BoverketClimateDeclarationPayload,
): string => {
  const { byggnad, klimat } = payload;
  const byggnadTags = [
    xmlTag('smahusBTA', byggnad.smahusBTA),
    xmlTag('flerbostadshusBTA', byggnad.flerbostadshusBTA),
    xmlTag('specialbostadBTA', byggnad.specialbostadBTA),
    xmlTag('forskolaBTA', byggnad.forskolaBTA),
    xmlTag('grundskolaBTA', byggnad.grundskolaBTA),
    xmlTag('gymnasieskolaBTA', byggnad.gymnasieskolaBTA),
    xmlTag('kontorBTA', byggnad.kontorBTA),
    xmlTag('handelBTA', byggnad.handelBTA),
    xmlTag('ovrigtBTA', byggnad.ovrigtBTA),
    xmlTag('vaningsplanOvanMark', byggnad.vaningsplanOvanMark),
    xmlTag('antalKallarPlan', byggnad.antalKallarPlan),
    xmlTag('garageArea', byggnad.garageArea),
    xmlTag('solcellerArea', byggnad.solcellerArea),
    xmlTag('balkongBTA', byggnad.balkongBTA),
    xmlTag('balkongOPA', byggnad.balkongOPA),
    xmlTag('loftgangarBTA', byggnad.loftgangarBTA),
    xmlTag('loftgangarOPA', byggnad.loftgangarOPA),
    xmlTag(
      'barandekonstruktionGrundlaggningBetong',
      byggnad.barandekonstruktionGrundlaggningBetong,
    ),
    xmlTag(
      'barandekonstruktionGrundlaggningStal',
      byggnad.barandekonstruktionGrundlaggningStal,
    ),
    xmlTag(
      'barandekonstruktionGrundlaggningTra',
      byggnad.barandekonstruktionGrundlaggningTra,
    ),
    xmlTag(
      'barandekonstruktionGrundlaggningOvrigt1Procent',
      byggnad.barandekonstruktionGrundlaggningOvrigt1Procent,
    ),
    xmlTag(
      'barandekonstruktionGrundlaggningOvrigt2Procent',
      byggnad.barandekonstruktionGrundlaggningOvrigt2Procent,
    ),
    xmlTag(
      'barandekonstruktionGrundlaggningOvrigt1Konstruktionsdel',
      byggnad.barandekonstruktionGrundlaggningOvrigt1Konstruktionsdel,
    ),
    xmlTag(
      'barandekonstruktionGrundlaggningOvrigt2Konstruktionsdel',
      byggnad.barandekonstruktionGrundlaggningOvrigt2Konstruktionsdel,
    ),
    xmlTag(
      'barandekonstruktionOvrigaBetong',
      byggnad.barandekonstruktionOvrigaBetong,
    ),
    xmlTag(
      'barandekonstruktionOvrigaStal',
      byggnad.barandekonstruktionOvrigaStal,
    ),
    xmlTag(
      'barandekonstruktionOvrigaTra',
      byggnad.barandekonstruktionOvrigaTra,
    ),
    xmlTag(
      'barandekonstruktionOvrigaOvrigt1Procent',
      byggnad.barandekonstruktionOvrigaOvrigt1Procent,
    ),
    xmlTag(
      'barandekonstruktionOvrigaOvrigt1Konstruktionsdel',
      byggnad.barandekonstruktionOvrigaOvrigt1Konstruktionsdel,
    ),
    xmlTag(
      'barandekonstruktionOvrigaOvrigt2Procent',
      byggnad.barandekonstruktionOvrigaOvrigt2Procent,
    ),
    xmlTag(
      'barandekonstruktionOvrigaOvrigt2Konstruktionsdel',
      byggnad.barandekonstruktionOvrigaOvrigt2Konstruktionsdel,
    ),
    xmlTag('energiklass', byggnad.energiklass),
  ];

  const klimatTags = [
    xmlTag(
      'barandekonstruktionsdelarGrundlaggningGK',
      klimat.barandekonstruktionsdelarGrundlaggningGK,
    ),
    xmlTag(
      'barandekonstruktionsdelarGrundlaggningSK',
      klimat.barandekonstruktionsdelarGrundlaggningSK,
    ),
    xmlTag(
      'barandekonstruktionsdelarOvrigaGK',
      klimat.barandekonstruktionsdelarOvrigaGK,
    ),
    xmlTag(
      'barandekonstruktionsdelarOvrigaSK',
      klimat.barandekonstruktionsdelarOvrigaSK,
    ),
    xmlTag('klimatskarmGK', klimat.klimatskarmGK),
    xmlTag('klimatskarmSK', klimat.klimatskarmSK),
    xmlTag('innervaggarGK', klimat.innervaggarGK),
    xmlTag('innervaggarSK', klimat.innervaggarSK),
    xmlTag('a4TransportGK', klimat.a4TransportGK),
    xmlTag('a4TransportSK', klimat.a4TransportSK),
    xmlTag('a5ByggspillGK', klimat.a5ByggspillGK),
    xmlTag('a5ByggspillSK', klimat.a5ByggspillSK),
    xmlTag('energiPaverkan', klimat.energiPaverkan),
    xmlTag(
      'upprakningAvBeraknadKlimatpaverkan',
      klimat.upprakningAvBeraknadKlimatpaverkan,
    ),
    xmlTag('tackningsgrad', klimat.tackningsgrad),
  ];

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<klimatdeklaration>',
    '<byggnad>',
    ...byggnadTags,
    '</byggnad>',
    '<klimat>',
    ...klimatTags,
    '</klimat>',
    '</klimatdeklaration>',
  ].join('\n');
};

/**
 * Build XML directly from design and optional version energy data.
 */
export const buildBoverketClimateDeclarationXmlFromDesign = (
  input: BuildingDesign | BoverketClimateDeclarationExportInput,
): string =>
  buildBoverketClimateDeclarationXml(
    buildBoverketClimateDeclarationPayloadFromDesign(input),
  );
